"use client";

export type AppleMusicDebugLevel = "info" | "warn" | "error";

export type AppleMusicDebugEntry = {
  seq: number;
  timestamp: string;
  level: AppleMusicDebugLevel;
  event: string;
  persist: boolean;
  details: Record<string, unknown>;
};

export const APPLE_MUSIC_DEBUG_EVENT = "imags:applemusic-debug";
const APPLE_MUSIC_DEBUG_BUFFER_LIMIT = 200;

let fallbackAppleMusicDebugSequence = 0;

declare global {
  interface Window {
    __IMAGS_APPLE_MUSIC_DEBUG_LOGS__?: AppleMusicDebugEntry[];
    __IMAGS_APPLE_MUSIC_DEBUG_SEQ__?: number;
  }
}

function nextAppleMusicDebugSequence() {
  if (typeof window === "undefined") {
    fallbackAppleMusicDebugSequence += 1;
    return fallbackAppleMusicDebugSequence;
  }

  const next =
    typeof window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ === "number"
      ? window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ + 1
      : fallbackAppleMusicDebugSequence + 1;
  window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ = next;
  fallbackAppleMusicDebugSequence = next;
  return next;
}

export function emitAppleMusicDebugEntry(entry: AppleMusicDebugEntry) {
  if (typeof window === "undefined") return entry;

  const buffer = Array.isArray(window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__)
    ? window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__
    : [];
  buffer.push(entry);
  if (buffer.length > APPLE_MUSIC_DEBUG_BUFFER_LIMIT) {
    buffer.splice(0, buffer.length - APPLE_MUSIC_DEBUG_BUFFER_LIMIT);
  }
  window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__ = buffer;
  if (
    typeof window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ !== "number" ||
    window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ < entry.seq
  ) {
    window.__IMAGS_APPLE_MUSIC_DEBUG_SEQ__ = entry.seq;
    fallbackAppleMusicDebugSequence = entry.seq;
  }
  window.dispatchEvent(
    new CustomEvent<AppleMusicDebugEntry>(APPLE_MUSIC_DEBUG_EVENT, {
      detail: entry,
    })
  );
  return entry;
}

export function appendAppleMusicDebugEntry({
  event,
  level = "info",
  persist = false,
  details = {},
}: {
  event: string;
  level?: AppleMusicDebugLevel;
  persist?: boolean;
  details?: Record<string, unknown>;
}) {
  return emitAppleMusicDebugEntry({
    seq: nextAppleMusicDebugSequence(),
    timestamp: new Date().toISOString(),
    level,
    event,
    persist,
    details,
  });
}

export function readAppleMusicDebugBuffer(limit?: number) {
  if (typeof window === "undefined") return [];
  const buffer = Array.isArray(window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__)
    ? window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__
    : [];
  if (typeof limit === "number" && Number.isFinite(limit) && limit > 0) {
    return buffer.slice(-Math.floor(limit));
  }
  return [...buffer];
}

export function clearAppleMusicDebugBuffer() {
  if (typeof window === "undefined") return;
  window.__IMAGS_APPLE_MUSIC_DEBUG_LOGS__ = [];
}
